/**
 * How a job identifies itself on the board.
 *
 * Imported ServiceM8 jobs rarely have a title — `job_description` lands in
 * `description`, not here — so "Untitled" / "Job" is noise. The dispatcher
 * needs where they are going and what they are doing.
 */

import { BRAND } from '../../../lib/brand.js'

type JobLabelSource = {
  title?: string | null
  client: { name: string }
  category?: { name: string } | null
  site?: {
    name?: string | null
    address?: { suburb: string | null; city?: string | null } | null
  } | null
}

export function jobClientName(job: JobLabelSource): string {
  return job.client.name
}

/** Locality for the board. Imported addresses often land in `city`. */
export function jobSuburb(job: JobLabelSource): string | null {
  return job.site?.address?.suburb ?? job.site?.address?.city ?? null
}

/**
 * The name a booking leads with: the property if it has one, otherwise the
 * client. Commercial work is "Modbury supermarket"; a house is the customer.
 */
export function jobPlaceName(job: JobLabelSource): string {
  const siteName = job.site?.name?.trim()
  return siteName || job.client.name
}

/**
 * Kind of work on the third line.
 *
 * Title wins over category because a job called "Bed bug treatment · 4 rooms"
 * is more useful than the category it was filed under. Suburb is appended only
 * when the card is leading with the client — a named property already says
 * where.
 */
export function jobServiceLine(job: JobLabelSource): string | null {
  const service = job.title?.trim() || job.category?.name?.trim() || null
  const suburb = jobSuburb(job)
  const namedSite = Boolean(job.site?.name?.trim())

  if (service && namedSite) return service
  if (service && suburb) return `${service} · ${suburb}`
  if (service) return service
  return namedSite ? null : suburb
}

/**
 * The priority word a card wears, or nothing.
 *
 * One helper because a booking and a parked chip must not disagree about which
 * jobs are shouting — the same job moves between the two.
 */
export function jobPriorityFlag(job: { priority: string }): string | null {
  if (job.priority === 'EMERGENCY') return 'Flag'
  return job.priority === 'HIGH' ? 'High' : null
}

/** One-line label for compact blocks and drag previews. */
export function jobHeadline(job: JobLabelSource): string {
  const place = jobPlaceName(job)
  const work = job.title?.trim() || job.category?.name?.trim() || null
  if (work) return `${place} · ${work}`

  const suburb = jobSuburb(job)
  return suburb && suburb.toLowerCase() !== place.toLowerCase() ? `${place} · ${suburb}` : place
}

/**
 * The SMS / email body for confirming a booking.
 *
 * One generic sentence for every service, which is the thing the client
 * specifically called out as confusing customers — a termite treatment and a
 * general spray need different expectations set. Replacing this with
 * per-service templates is the `tailored-confirmations` task, and it needs
 * outbound SMS first, which is not built.
 */
export function bookingConfirmMessage(visit: {
  startAt: Date
  assignments: Array<{ user: { name: string } }>
}): string {
  const lead = visit.assignments[0]?.user.name
  const when = visit.startAt.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })
  return `Hi, this is ${BRAND.name} confirming your appointment today at ${when}${lead ? ` with ${lead}` : ''}. Reply if you need to change it.`
}

/** How each kind of non-working time reads on the board and in the form. */
export const LEAVE_TYPE_LABEL: Record<import('./types.js').LeaveType, string> = {
  ANNUAL: 'Annual leave',
  SICK: 'Sick leave',
  UNPAID: 'Unpaid leave',
  PUBLIC_HOLIDAY: 'Public holiday',
  OTHER: 'Unavailable',
}
